<template>
  <div class="banner-carousel" v-if="items && items.length" ref="root">
    <vue-slick-carousel
      ref="slick"
      :dots="false"
      :infinite="true"
      :arrows="true"
      :autoplay="true"
      :autoplay-speed="8000"
      :speed="720"
      css-ease="cubic-bezier(0.22, 1, 0.36, 1)"
      :slides-to-show="1"
      :slides-to-scroll="1"
      :center-mode="true"
      :center-padding="centerPadding"
      :variable-width="true"
      :initial-slide="1"
      :adaptive-height="false"
      :rows="1"
      @afterChange="onAfterChange"
      class="carousel-container"
    >
      <template #prevArrow>
        <div class="custom-arrow left">
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="white" />
          </svg>
        </div>
      </template>
      <template #nextArrow>
        <div class="custom-arrow right">
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none">
            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" fill="white" />
          </svg>
        </div>
      </template>

      <div
        v-for="(banner, index) in items"
        :key="banner.id || index"
        class="banner-slide"
        :style="slideStyle"
      >
        <a :href="safeHttpUrl(banner.link)" target="_blank" rel="noopener noreferrer" class="banner-link" v-if="safeHttpUrl(banner.link)" @contextmenu="openBannerInNewTab($event, banner)">
          <div class="banner-media" :style="mediaStyle">
            <img
              class="banner-image"
              :src="banner.image"
              :alt="banner.title || 'Banner'"
              @error="onImageError($event)"
              loading="lazy"
            />
          </div>
        </a>
        <div v-else class="banner-media" :style="mediaStyle">
          <img
            class="banner-image"
            :src="banner.image"
            :alt="banner.title || 'Banner'"
            @error="onImageError($event)"
            loading="lazy"
          />
        </div>
      </div>
    </vue-slick-carousel>
  </div>
  <div v-else-if="loading" class="banner-carousel skeleton">
    <div class="skeleton-slide"></div>
  </div>
</template>

<script>
import VueSlickCarousel from 'vue-slick-carousel';
import 'vue-slick-carousel/dist/vue-slick-carousel.css';
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css';
import { openSafeHttpUrl, sanitizeHttpUrl } from '@/utils/safeNavigation';

export default {
  name: 'BannerCarousel',
  components: { VueSlickCarousel },
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    height: { type: Number, default: 597 },
    cardWidth: { type: Number, default: 258 },
  },
  data() {
    return {
      screenWidth: window.innerWidth,
      containerWidth: 0,
      currentIndex: 0,
    };
  },
  computed: {
    desiredItemWidth() {
      // Desktop/tablet seguem o multiplicador atual; mobile limita pela largura do container
      // para garantir 1 card com "peek" leve e sem overflow.
      let multiplier = 3.5; // desktop padrao com largura ampliada em 25%

      if (this.screenWidth <= 768) {
        // Mobile: largura reduzida para evitar distorção do banner
        return 375;
      }

      if (this.screenWidth <= 1024)
        multiplier = 1.625; // tablet com largura ampliada em 25%

      return Math.round(this.cardWidth * multiplier);
    },
    mediaStyle() {
      // Aumenta a altura em ~26.5% (15% anterior + 10% adicional)
      const base = this.screenWidth <= 768 ? 180 : this.height;
      const h = Math.round(base * 1.265);
      return {
        height: `${h}px`,
        backgroundColor: '#111',
      };
    },
    slideStyle() {
      const base = this.screenWidth <= 768 ? 180 : this.height;
      const h = Math.round(base * 1.265);
      return {
        height: `${h}px`,
        width: `${this.desiredItemWidth}px`,
      };
    },
    centerPadding() {
      // Padding fixo para evitar overflow e segunda linha
      if (this.screenWidth <= 768) return '12px';
      if (this.screenWidth <= 1024) return '40px';
      return '120px';
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
    this.measureWidth();
    this.$nextTick(() => {
      const slick = this.$refs.slick;
      if (slick && typeof slick.goTo === 'function') {
        slick.goTo(1);
        this.currentIndex = 1;
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    safeHttpUrl: sanitizeHttpUrl,
    onResize() {
      this.screenWidth = window.innerWidth;
      this.measureWidth();
    },
    onAfterChange(index) {
      // Atualiza o índice do slide central ativo
      this.currentIndex = typeof index === 'number' ? index : 0;
    },
    measureWidth() {
      this.$nextTick(() => {
        const root = this.$refs.root;
        this.containerWidth = root && root.clientWidth ? root.clientWidth : 0;
      });
    },
    prev() {
      const slick = this.$refs.slick;
      if (slick && typeof slick.prev === 'function') slick.prev();
    },
    next() {
      const slick = this.$refs.slick;
      if (slick && typeof slick.next === 'function') slick.next();
    },
    onImageError(event) {
      const img = event.target;
      img.style.display = 'none'; // hide broken img tag; background still shows
    },
    openBannerInNewTab(event, banner) {
      event.preventDefault();
      openSafeHttpUrl(banner.link);
    }
  }
};
</script>

<style scoped>
.banner-carousel {
  width: 100%;
  position: relative;
  margin: 24px 0;
}

.carousel-container ::v-deep .slick-slider,
.carousel-container ::v-deep .slick-list,
.carousel-container ::v-deep .slick-track {
  height: 100%;
}

.carousel-container ::v-deep .slick-slide {
  padding: 0 10px;
}

.carousel-container ::v-deep .slick-list {
  overflow: hidden;
}

.carousel-container ::v-deep .slick-track {
  white-space: nowrap;
}

.carousel-container ::v-deep .slick-slide {
  float: none !important;
  display: inline-block !important;
  vertical-align: top;
}


.carousel-container ::v-deep .slick-prev:before,
.carousel-container ::v-deep .slick-next:before {
  display: none !important;
}

.banner-slide {
  position: relative;
  width: 100%;
  opacity: 1;
  transform: scale(0.94);
  transition: transform 720ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.carousel-container ::v-deep .slick-center .banner-slide {
  opacity: 1;
  transform: scale(1);
}

.banner-link {
  text-decoration: none;
}

.banner-media {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #111;
  box-sizing: border-box;
    border: 2px solid #404040;
}

.carousel-container ::v-deep .slick-center .banner-image {
  transform: scale(1.01);
}

.banner-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transition: transform 720ms cubic-bezier(0.22, 1, 0.36, 1);
}

.custom-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.55);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 5;
  transition: background 0.2s ease;
}

.custom-arrow:hover {
  background: rgba(0, 0, 0, 0.75);
}

.custom-arrow { display: flex !important; }
.custom-arrow.left { left: 16px; }
.custom-arrow.right { right: 16px; }

/* Dots */
.carousel-container ::v-deep .slick-dots li button:before {
  color: #fff;
  opacity: 0.6;
}
.carousel-container ::v-deep .slick-dots li.slick-active button:before {
  color: #ffd700;
  opacity: 1;
}

.skeleton {
  min-height: 756px;
}

.skeleton-slide {
  width: 100%;
  min-height: 756px;
  border-radius: 12px;
  background: linear-gradient(90deg, #2b2b2b 25%, #3b3b3b 50%, #2b2b2b 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite linear;
}

@keyframes skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 1024px) {
  .custom-arrow { width: 42px; height: 42px; }
}

@media (max-width: 768px) {
  .banner-carousel {
    margin: 8px 0;
    max-height: 228px;
  }
  .custom-arrow { display: none !important; }
  .banner-media {
    min-height: unset;
    height: 228px;
  }
  .banner-image {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
  .carousel-container ::v-deep .slick-list {
    max-height: 228px;
  }
}
</style>
